import * as React from 'react';
import { trapFocus, releaseFocusTrap } from '../../../../../utils/FocusTrap';
import classNames from "classnames";
import AqButton from "../../Aq/Button";
import { handleActions } from "../../../helpers/Doc/handleActions";

interface RatingStarProps {
  maxValue: number;
  onClick: (i: number) => void;
  value: number;
}

function starIndexForValue(maxValue: number, value: number) {
  return value > 0 ? value - 1 : 0;
}

function RatingStars(props: RatingStarProps) {
  const { maxValue, onClick, value } = props;
  const [focusedIndex, setFocusedIndex] = React.useState<number>(() =>
    starIndexForValue(maxValue, value),
  );
  const starRefs = React.useRef<(HTMLSpanElement | null)[]>([]);

  React.useEffect(() => {
    setFocusedIndex(starIndexForValue(maxValue, value));
  }, [value, maxValue]);

  const handleClick = (i: number, e: React.MouseEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    onClick(i + 1);
    setFocusedIndex(i);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLSpanElement>) => {
    let newIndex = i;
    if (e.key === 'Enter' || e.key === ' ') {
      handleClick(i, e);
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      newIndex = i > 0 ? i - 1 : maxValue - 1;
      onClick(newIndex + 1);
      starRefs.current[newIndex]?.focus();
      setFocusedIndex(newIndex);
      e.preventDefault();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      newIndex = i < maxValue - 1 ? i + 1 : 0;
      onClick(newIndex + 1);
      starRefs.current[newIndex]?.focus();
      setFocusedIndex(newIndex);
      e.preventDefault();
    }
  };

  const pressedIndex = value > 0 ? value - 1 : null;

  const stars = [];
  for (let i = 0; i < maxValue; i += 1) {
    const isActive = value > 0 && i < value;
    const isFocused = focusedIndex === i;
    const className = classNames(
      "doc-rating-bar-star",
      {"doc-rating-bar-star-active": isActive},
      {"doc-rating-bar-star-focused": isFocused}
    );
    stars.push(
      <span
        className={className}
        key={i}
        ref={el => starRefs.current[i] = el}
        tabIndex={0}
        aria-label={`Rate ${i + 1} out of ${maxValue}`}
        role="button"
        aria-pressed={pressedIndex !== null && i === pressedIndex}
        onClick={(e) => handleClick(i, e)}
        onKeyDown={(e) => handleKeyDown(i, e)}
        onFocus={() => setFocusedIndex(i)}
        onBlur={() => setFocusedIndex(null)}
        style={isFocused ? {outline: '2px solid #1976d2', outlineOffset: '2px'} : {}}
      >
        ☆
      </span>
    );
  }

  return (
    <div className={"doc-rating-bar"} role="group" aria-label="Rating bar">
      {stars}
    </div>
  );
}

interface DocFeedbackModalProps {
  displayContext: any;
  userContext: any;
  onClick: Array<any>;
  onSubmitFinish: () => void;
}
export function DocFeedbackModal(props: DocFeedbackModalProps) {
  const {
    displayContext,
    userContext,
    onClick,
    onSubmitFinish,
  } = props;

  const [rating1, setRating1] = React.useState(0);
  const [rating2, setRating2] = React.useState(0);
  const [comment, setComment] = React.useState("");

  // Focus trap logic
  const modalRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    const node = modalRef.current;
    triggerRef.current = document.activeElement as HTMLElement;
    const mainEl = document.getElementById('main');
    if (mainEl) mainEl.setAttribute('aria-hidden', 'true');
    if (node) trapFocus(node);
    const focusableSelectors = [
      'a[href]',
      'area[href]',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ];
    const enforceFocus = (e: FocusEvent) => {
      if (!node) return;
      if (!node.contains(e.target as Node)) {
        const focusable = node.querySelectorAll(focusableSelectors.join(','));
        const visibleFocusable = Array.from(focusable).filter(el => (el as HTMLElement).offsetParent !== null);
        if (visibleFocusable.length > 0) {
          (visibleFocusable[0] as HTMLElement).focus();
        } else {
          node.focus();
        }
      }
    };
    document.addEventListener('focusin', enforceFocus);
    if (node) node.setAttribute('tabindex', '-1');
    return () => {
      if (mainEl) mainEl.removeAttribute('aria-hidden');
      if (node) releaseFocusTrap(node);
      if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
        triggerRef.current.focus();
      }
      document.removeEventListener('focusin', enforceFocus);
    };
  }, []);

  return (
    <div className="feedback-modal-background">
      <div
        className="feedback-modal"
        ref={modalRef}
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        aria-labelledby="feedback-modal-heading"
        aria-describedby="feedback-modal-content"
      >
        <div className="doc-section-body" id="feedback-modal-content">
          <header>
            <p id="feedback-modal-heading" className="feedback-modal-heading">You're almost done...</p>
            <h2 className="feedback-modal-subheading">GIVE US YOUR FEEDBACK TO COMPLETE THIS CASE</h2>
          </header>
          <div className="feedback-modal-blue-separator"></div>
          <h3 id="modal-desc" className="feedback-modal-description-heading">We want to know what you think.</h3>
          <p className="feedback-modal-description">
            Your rating and optional comment will be reviewed by Aquifer staff and shared with the educators who regularly review this case.
          </p>
          <h4 className="feedback-modal-value" style={{fontSize: "16px", fontWeight: 700, fontFamily: "Roboto", marginLeft: "30px"}}>This case was a valuable use of my time.</h4>
          <div style={{marginLeft: "45px"}}>
            <RatingStars
              maxValue={5}
              value={rating1}
              onClick={setRating1}
            />
          </div>
          <h4 id="modal-apply" style={{fontSize: "16px", fontWeight: 700, fontFamily: "Roboto", marginLeft: "30px"}}>I will apply what I learned to patient care.</h4>
          <div style={{marginLeft: "45px"}}>
            <RatingStars
              maxValue={5}
              value={rating2}
              onClick={setRating2}
            />
          </div>
          <h5
            style={{
              fontWeight: 700,
              fontSize: "12px",
              fontFamily: "Roboto Condensed",
              color: "black",
              marginBottom: "8px"
            }}
          >
            Please note any comments, suggestions, or questions related to the case (optional):
          </h5>
          <textarea
            cols={40}
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            aria-label="Please note any comments, suggestions, or questions related to the case (optional)"
          />
          <AqButton
            disabled={rating1 < 1 || rating2 < 1}
            onClick={() => {
              onSubmitFinish();
              const ratingBarState = {
                feedback_card_1_rating_bar: {value: rating1},
                feedback_card_2_rating_bar: {value: rating2},
              };
              if (comment !== "") {
                userContext.addState({
                  ...ratingBarState,
                  feedback_card_1_text_input: {value: comment},
                });
              } else {
                userContext.addState(ratingBarState)
              }
              setTimeout(() => {
                const handleActionsArgs = {
                  displayContext,
                  userContext,
                }
                handleActions(onClick, handleActionsArgs);
              }, 1000);
            }}
          >
            Submit & Finish Case
          </AqButton>
        </div>
      </div>
    </div>
  )
}
